import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Spinner,
  Flex,
  SimpleGrid,
  Box,
  Alert,
  AlertIcon,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import ReferralsApi from '../../../../api/referrals';
import ReferredUsersTable from './ReferredUsersTable';

const ReferralDetailsModal = ({ user, isOpen, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && user && user.userId) {
      fetchUserReferralDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const fetchUserReferralDetails = async () => {
    setLoading(true);
    try {
      const response = await ReferralsApi.getUserReferralDetails(user.userId);
      if (response && response.data) {
        setDetails(response.data);
      }
    } catch (error) {
      toast({
        title: 'שגיאה בטעינת פרטי ההפניות',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, color, subtitle }) => (
    <Stat>
      <StatLabel fontSize="sm" color="gray.600">{title}</StatLabel>
      <StatNumber fontSize="2xl" color={`${color}.500`}>{value}</StatNumber>
      {subtitle && <StatHelpText fontSize="xs">{subtitle}</StatHelpText>}
    </Stat>
  );

  const ConversionChart = ({ stats }) => (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={4}>Referral Conversion</Text>
      
      <Box mb={4}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text>Registered Users</Text>
          <Badge colorScheme="blue">{stats.totalReferrals}</Badge>
        </Flex>
        <Progress value={100} colorScheme="blue" size="lg" />
      </Box>
      
      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <Text>Made Applications ✓</Text>
          <Badge colorScheme="green">{stats.withApplications}</Badge>
        </Flex>
        <Progress 
          value={(stats.withApplications / stats.totalReferrals) * 100} 
          colorScheme="green" 
          size="lg" 
        />
        <Text fontSize="sm" color="gray.600" mt={1}>
          {stats.conversionRate}% conversion rate
        </Text>
      </Box>
    </Box>
  );

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          <HStack spacing={4}>
            <Text>Referral Details - {user.fullName}</Text>
            <Badge colorScheme="purple" variant="subtle">
              {user.referralCode}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton left="1rem" right="auto" />
        <ModalBody pb={4}>
          {loading ? (
            <Flex justify="center" align="center" height="400px">
              <Spinner size="lg" />
            </Flex>
          ) : details ? (
            <Tabs index={activeTab} onChange={setActiveTab}>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>
                  Registered Only
                  <Badge ml={2} colorScheme="red">
                    {details.referredUsers?.registeredOnly?.length || 0}
                  </Badge>
                </Tab>
                <Tab>
                  With Applications ✓
                  <Badge ml={2} colorScheme="green">
                    {details.referredUsers?.withApplications?.length || 0}
                  </Badge>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                      <StatCard
                        title="Total Referrals"
                        value={details.stats?.totalReferrals || 0}
                        color="blue"
                      />
                      <StatCard
                        title="Successful Referrals"
                        value={details.stats?.withApplications || 0}
                        color="green"
                        subtitle="(Made applications)"
                      />
                      <StatCard
                        title="Pending Referrals"
                        value={details.stats?.registeredOnly || 0}
                        color="red"
                        subtitle="(No applications yet)"
                      />
                      <StatCard
                        title="Conversion Rate"
                        value={`${details.stats?.conversionRate || 0}%`}
                        color="purple"
                      />
                    </SimpleGrid>

                    <ConversionChart stats={details.stats} />

                    {details.stats?.avgDaysToFirstApplication > 0 && (
                      <Box>
                        <Text fontSize="md" fontWeight="semibold" mb={2}>
                          Average Time to First Application
                        </Text>
                        <Text fontSize="lg" color="blue.500">
                          {details.stats.avgDaysToFirstApplication} days
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Alert status="warning">
                      <AlertIcon />
                      These users registered but haven't made any transfer applications yet
                    </Alert>
                    <ReferredUsersTable 
                      users={details.referredUsers?.registeredOnly || []} 
                      showApplicationStatus={false}
                    />
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Alert status="success">
                      <AlertIcon />
                      These users made applications - they count as successful referrals!
                    </Alert>
                    <ReferredUsersTable 
                      users={details.referredUsers?.withApplications || []} 
                      showApplicationStatus={true}
                    />
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <Text textAlign="center" py={8} color="gray.500">
              No referral details available
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ReferralDetailsModal;
